<template>
  <div class="confirm-popup">
    <div class="overlay" @click="handleToggleConfirmationModalInjected && handleToggleConfirmationModalInjected()" />
    <div class="confirm-content">
      <h2 class="confirm-content-title">{{ $t('craftingConfirmationTitle') }}</h2>
      <div
        class="confirm-close-btn"
        @click="handleToggleConfirmationModalInjected && handleToggleConfirmationModalInjected()"
      />
      <div class="tw:flex tw:flex-col tw:gap-6">
        <div class="tw:flex tw:flex-col tw:gap-2">
          <h4 class="tw:m-0! confirm-content-token-name tw:leading-none">
            {{ getCleanRecipeName(recipe!, recipeIdPrefix) }}
          </h4>

          <p v-if="recipe?.duration" class="confirm-content-token-time">
            {{ $t('willBeReadyIn') }}:
            <vue-countdown
              ref="countdownRef"
              v-slot="{ days, hours, minutes, seconds }"
              :time="displayReadyTime"
              :auto-start="queueTimeInitial > 0"
            >
              <template v-if="BigNumber(days).isGreaterThan(0)">
                {{ Formatters.text.zeroPrefix(days) + t('coreDaysPostfix') }}
                {{ BigNumber(hours).isGreaterThan(0) ? Formatters.text.zeroPrefix(hours) + t('coreHoursPostfix') : '' }}
              </template>

              <template v-else-if="BigNumber(hours).isGreaterThan(0)">
                {{ Formatters.text.zeroPrefix(hours) + t('coreHoursPostfix') }}
                {{
                  BigNumber(minutes).isGreaterThan(0) ? Formatters.text.zeroPrefix(minutes) + t('coreMinPostfix') : ''
                }}
              </template>

              <template v-else-if="BigNumber(minutes).isGreaterThan(0)">
                {{ Formatters.text.zeroPrefix(minutes) + t('coreMinPostfix') }}
                {{
                  BigNumber(seconds).isGreaterThan(0) ? Formatters.text.zeroPrefix(seconds) + t('coreSecPostfix') : ''
                }}
              </template>

              <template v-else>
                {{ Formatters.text.zeroPrefix(seconds) + t('coreSecPostfix') }}
              </template>
            </vue-countdown>
          </p>
          <div class="confirm-content-items">
            <div
              v-for="product of recipe?.products"
              :key="product.tokenAddress"
              class="tw:inline-flex tw:flex-col tw:justify-between tw:items-center tw:p-3 confirm-content-item"
            >
              <img
                :src="$getIconToken(product, BigNumber(recipe?.recipeId || 0).toNumber())"
                :alt="props.tokenMetadata.label"
              />
              <p v-if="!isNft" class="tw:font-grenze tw:font-bold tw:text-white tw:text-xl tw:leading-none">
                <template v-if="new BigNumber(product.quantityMin).eq(product.quantityMax)">
                  {{ new BigNumber(product.quantityMax).multipliedBy(craftingMultiplier) }}
                </template>
                <template v-else>
                  {{ new BigNumber(product.quantityMin).decimalPlaces(1).multipliedBy(craftingMultiplier) }}-{{
                    new BigNumber(product.quantityMax).decimalPlaces(1).multipliedBy(craftingMultiplier)
                  }}
                </template>
              </p>
              <p v-else class="valuable">
                {{ $t(`core${Formatters.text.firstLetterUppercase($getProductUniqueness(product))}`) }}
                <span v-if="craftingMultiplier > 1"> × {{ craftingMultiplier }} </span>
              </p>
            </div>
          </div>
        </div>
        <div>
          <div v-show="!wasHiddenInitially" class="confirm-content-confirm-block">
            <p
              class="confirm-content-confirm-text"
              v-html="recipe?.duration ? $t('craftingConfirmationText') : $t('craftingConfirmationTextImmediate')"
            />
            <div class="confirm-content-checkbox">
              <el-checkbox id="agree-cb" v-model="agreeToTerms" type="checkbox" class="checkbox" size="large">
                {{ $t('craftingCheckboxAgree') }}
              </el-checkbox>
            </div>
          </div>

          <div v-show="!wasHiddenInitially" class="auto-confirm-content-checkbox">
            <el-checkbox id="auto-agree-cb" v-model="autoConfirmCrafting" type="checkbox" class="checkbox" size="large">
              {{ $t('autoConfirmCraftingTitle') }}
            </el-checkbox>
            <p class="auto-confirm-content-confirm-text">
              {{ $t('autoConfirmCraftingCheckboxDescription') }}
            </p>
          </div>

          <button
            class="btn-primary produce"
            :disabled="!agreeToTerms || isItemCraftingInjected"
            @click="produceItemInjected && recipe && produceItemInjected(recipe)"
          >
            {{ $t('craftingProduce') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import VueCountdown from '@chenfengyuan/vue-countdown';
import useEnvs from '~/composables/useEnvs';
import type { RecipeProduct, RecipeProps, RunningRecipeProps } from '~/types/crafting';
import { type TTokensConfigItem } from '~/utils/constants';
import { BigNumber } from 'bignumber.js';
import { Formatters } from '~/utils/formatters/formatters';

const { t } = useI18n();
const productValue = ref('');
const countdownRef = ref<any>();
const finalReadyTime = ref<number | null>(null);
const now = ref(Date.now());
setInterval(() => {
  now.value = Date.now();
}, 1000);

const { shouldBeHidden, currentFlags, saveAllFlags } = useAutoConfirmFlags('agreeToCraftingRules');
const wasHiddenInitially = shouldBeHidden.value;

const config = useRuntimeConfig();
const { blockchain } = useEnvs();
const recipeIdPrefix = computed(() => {
  const env = config?.public.env;
  return env === 'prod' ? 'prodRecipeId' : env === 'demo' ? 'demoRecipeId' : 'testnetRecipeId';
});
const autoConfirmCrafting = computed({
  get() {
    return currentFlags.value.agreeToCraftingRules;
  },
  set(newValue: boolean) {
    currentFlags.value.agreeToCraftingRules = newValue;
    saveAllFlags();
  }
});
const props = defineProps<{
  tokenMetadata: TTokensConfigItem;
  recipe?: RecipeProps;
  craftingMultiplier: number;
  shipIndex?: number;
}>();

const { data: runningRecipes } = useNuxtData<RunningRecipeProps[]>('recipes/in-queue');

const lastRunningRecipeEndTime = computed(() =>
  Math.max(...(runningRecipes.value?.map((rec) => new Date(rec.craftEndTime).getTime()) || []))
);

const queueTimeInitial = computed(() =>
  BigNumber(lastRunningRecipeEndTime.value - now.value).isPositive() ? lastRunningRecipeEndTime.value - now.value : 0
);

watch(queueTimeInitial, (newVal) => {
  if (newVal <= 0 && finalReadyTime.value === null) {
    finalReadyTime.value = recipeDurationMs.value;
    countdownRef.value?.pause();
  }
});

const recipeDurationMs = computed(() =>
  BigNumber(props.recipe?.duration || 0)
    .multipliedBy(1000)
    .multipliedBy(props.craftingMultiplier)
    .toNumber()
);
const displayReadyTime = computed(() => {
  if (finalReadyTime.value !== null) {
    return finalReadyTime.value;
  }
  return BigNumber(queueTimeInitial.value).plus(recipeDurationMs.value).toNumber();
});

// const readyTime = computed(() =>
//   BigNumber(queueTimeInitial.value)
//     .plus(
//       BigNumber(props.recipe?.duration || 0)
//         .multipliedBy(1000)
//         .multipliedBy(props.craftingMultiplier)
//     )
//     .toNumber()
// );

const isNft = computed(() => {
  if (!props.recipe) return false;

  return (
    props.recipe &&
    props.recipe.products.every(
      (product: RecipeProduct) => product.nftValueMin !== null && product.nftValueMax !== null
    )
  );
});

const agreeToTerms = ref(shouldBeHidden.value);

watch(
  shouldBeHidden,
  (newValue) => {
    if (newValue) {
      agreeToTerms.value = true;
    } else {
      agreeToTerms.value = false;
    }
  },
  { immediate: true }
);

const handleToggleConfirmationModalInjected = inject<() => void>('handleToggleConfirmationModal');
const produceItemInjected = inject<(recipe: RecipeProps) => void>('handleProduceItem');
const isItemCraftingInjected = inject<boolean>('isItemCrafting');

function getItemValue() {
  if (!props.recipe) return '';

  const product = props.recipe.products[0];
  const value = getNftValueByAddress(blockchain.contracts, product);

  productValue.value = value;
}

getItemValue();
</script>

<style lang="scss" scoped>
.confirm-popup {
  position: absolute;
  top: 0;
  left: 0;

  z-index: 1999;
  height: 100%;
  width: 100%;

  border-radius: 15px;

  display: flex;

  @media screen and (max-width: 600px) {
    overflow: auto;
  }
}

.confirm-close-btn {
  cursor: pointer;
  position: absolute;
  top: 25px;
  right: 20px;
  z-index: 9999;
  background-image: url('~/public/img/icons/bone_close.svg');
  width: 20px;
  height: 20px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 50%;

  &:hover {
    opacity: 0.6;
  }
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: #000;
  opacity: 0.7;
}

.confirm-content {
  position: relative;
  z-index: 3;
  background-color: rgba(2, 27, 38);
  width: 100%;
  max-width: 683px;
  margin: auto;
  padding: 10px;
  border-radius: 10px;

  @media screen and (max-width: 900px) {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
  }

  &-title {
    font-family: Grenze;
    font-size: 32px;
    font-weight: 700;
    line-height: 47.36px;
    text-align: center;
    margin: 35px 0 25px;
    color: #fff;

    @media screen and (max-width: 479px) {
      font-size: 28px;
    }
  }

  &-token-name {
    font-family: Grenze;
    font-size: 26px;
    font-weight: 700;
    line-height: 38.48px;
    text-align: center;
  }

  &-token-time {
    font-family: Grenze;
    font-size: 24px;
    font-weight: 600;
    line-height: 26.4px;
    text-align: center;
    color: #49ddaa;
  }

  &-confirm-block {
    margin-left: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #0083ad;
  }

  &-confirm-text {
    font-family: Grenze;
    font-size: 20px;
    font-weight: 500;
    line-height: 22px;
    margin-bottom: 20px;
    color: #fff;

    > * {
      color: #0083ad !important;
    }
  }

  &-checkbox {
    font-size: 20px;
    font-weight: 500;
    line-height: 29.6px;
    font-family: Grenze;

    @media screen and (max-width: 479px) {
      margin: 10px auto;
    }
  }

  &-items {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &-item {
    width: 120px;
    height: 120px;
    border: 1px solid #1e353e;
    border-radius: 12px;
    box-shadow: 2px 4px 5px 0px #00000040;
    background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);

    img {
      height: 70px;
      width: 70px;
      border-radius: 7px;
    }

    .valuable {
      color: #ffe604;
      font-size: 1.2rem;
      padding-top: 12px;
      text-transform: capitalize;
    }
  }

  .btn-primary {
    display: block;
    margin: auto;
    width: 200px;

    &.produce {
      @media screen and (max-width: 900px) {
        width: 80%;
      }
    }
  }
}

.auto-confirm-content-checkbox {
  padding-top: 20px;
  margin-left: 20px;
}

.auto-confirm-content-confirm-text {
  font-family: Grenze;
  font-weight: 300;
  font-size: 14px;
  line-height: 100%;
  color: #838d92;
  margin: 20px 0;
}
</style>
